/**
 * Phase 21β-2.6 — Vertragsvorlagen Block-Editor (/vertragsvorlagen/:id/bearbeiten).
 *
 * Layout (100vh, kein globales Scrollen):
 *   ┌── Topbar: ← · Name · Status · Speichern · PDF · DOCX ─────────┐
 *   ├── Settings-Bar (Typ, Schrift, Margins) ───────────────────────┤
 *   ├── 320px ────┬─────────────── 1fr ─────────────────────────────┤
 *   │ Block-Liste │  Aktiver Block: Titel, Body (TipTap), Optional? │
 *   │ + ↑↓ + ⓧ    │  Kategorie, Notiz                                │
 *   └─────────────┴───────────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useEditor, EditorContent } from "@tiptap/react";

import api, { formatApiError, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useConfirm } from "@/components/ConfirmDialog";
import { buildEditorExtensions } from "@/components/editor/extensions";
import WordRibbon from "@/components/editor/WordRibbon";
import SnippetPicker from "@/components/editor/SnippetPicker";
import CategorySelect from "@/components/editor/CategorySelect";
import {
  CaretLeft, FloppyDisk, FilePdf, FileDoc, Plus, ArrowUp, ArrowDown,
  Trash, Eye, ToggleLeft, ToggleRight, Stack,
} from "@phosphor-icons/react";

const CONTRACT_TYPES = [
  { key: "wohnraum", label: "Wohnraum" },
  { key: "gewerbe", label: "Gewerbe" },
  { key: "stellplatz", label: "Stellplatz" },
  { key: "garage", label: "Garage" },
  { key: "sonstige", label: "Sonstige" },
];

export default function ContractTemplateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirmDialog = useConfirm();

  const [tpl, setTpl] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [snippetPickerOpen, setSnippetPickerOpen] = useState(false);
  // Suppresses TipTap onUpdate while we set content programmatically
  // (initial load + switching active block + post-save reload).
  const editorReadyRef = useRef(false);

  const activeBlock = useMemo(
    () => blocks.find((b) => b.id === activeBlockId) || null,
    [blocks, activeBlockId]
  );

  // Load
  const load = useCallback(async (preferredActiveId) => {
    editorReadyRef.current = false;
    try {
      const { data } = await api.get(`/contract-templates/${id}`);
      setTpl({ ...data, blocks: undefined }); // metadata only on tpl
      setBlocks(data.blocks || []);
      // Restore previously focused block (e.g. after Save) when possible
      const list = data.blocks || [];
      const focusId = (preferredActiveId && list.find((b) => b.id === preferredActiveId))
        ? preferredActiveId
        : (list[0]?.id || null);
      setActiveBlockId(focusId);
      setDirty(false);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/vertragsvorlagen");
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  // TipTap editor for currently active block body
  const editor = useEditor({
    extensions: buildEditorExtensions({
      placeholder: "Inhalt des Bausteins – verwende {{macros}} für dynamische Werte.",
    }),
    content: activeBlock?.body || "",
    editorProps: {
      attributes: {
        class: "tiptap-doc focus:outline-none min-h-[400px] px-6 py-5",
      },
    },
    onUpdate: ({ editor: e }) => {
      if (!editorReadyRef.current) return;
      if (!activeBlockId) return;
      const html = e.getHTML();
      setBlocks((prev) => prev.map((b) =>
        b.id === activeBlockId ? { ...b, body: html } : b
      ));
      setDirty(true);
    },
  });
  // re-sync editor when active block changes
  useEffect(() => {
    if (editor && activeBlock) {
      editorReadyRef.current = false;
      if (editor.getHTML() !== (activeBlock.body || "")) {
        editor.commands.setContent(activeBlock.body || "", false);
      }
      // Allow onUpdate to register changes from the next user keystroke onward
      setTimeout(() => { editorReadyRef.current = true; }, 30);
    }
  }, [activeBlockId, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mutators
  const updateBlockField = (field, value) => {
    if (!activeBlockId) return;
    setBlocks((prev) => prev.map((b) =>
      b.id === activeBlockId ? { ...b, [field]: value } : b
    ));
    setDirty(true);
  };
  const updateTplField = (field, value) => {
    setTpl((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const moveBlock = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[target]] = [next[target], next[idx]];
    // re-number sort
    next.forEach((b, i) => { b.sort = (i + 1) * 10; });
    setBlocks(next);
    setDirty(true);
  };

  const addBlock = () => {
    const nb = {
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      title: "Neuer Baustein",
      body: "<p></p>",
      optional: false,
      enabled: true,
      category: "",
      sort: (blocks.length + 1) * 10,
      _isNew: true,
    };
    setBlocks((prev) => [...prev, nb]);
    setActiveBlockId(nb.id);
    setDirty(true);
  };

  const deleteBlock = async (b) => {
    const ok = await confirmDialog({
      title: `Baustein „${b.title || "Ohne Titel"}" löschen?`,
      description: "Die Änderung wird beim nächsten Speichern wirksam.",
      confirmText: "Löschen", tone: "danger",
    });
    if (!ok) return;
    setBlocks((prev) => {
      const next = prev.filter((x) => x.id !== b.id);
      if (activeBlockId === b.id) {
        setActiveBlockId(next.length > 0 ? next[0].id : null);
      }
      return next;
    });
    setDirty(true);
  };

  // Save — push everything: metadata + blocks
  const save = async () => {
    if (!tpl) return;
    setSaving(true);
    try {
      // 1. Metadata PATCH
      await api.patch(`/contract-templates/${id}`, {
        name: tpl.name,
        contract_type: tpl.contract_type,
        description: tpl.description || "",
        status: tpl.status || "draft",
        page_margin_top_mm: tpl.page_margin_top_mm,
        page_margin_bottom_mm: tpl.page_margin_bottom_mm,
        page_margin_left_mm: tpl.page_margin_left_mm,
        page_margin_right_mm: tpl.page_margin_right_mm,
        font_family: tpl.font_family,
        font_size_pt: tpl.font_size_pt,
        line_height: tpl.line_height,
        // Phase 21β-2.7 — Word-Ribbon Layout-Tab fields
        page_orientation: tpl.page_orientation || "portrait",
        page_format: tpl.page_format || "a4",
        page_columns: tpl.page_columns || 1,
        hyphenation: !!tpl.hyphenation,
      });
      // 2. Sync blocks: fetch current, compute add/update/delete
      const { data: current } = await api.get(`/contract-templates/${id}`);
      const currentBlocks = current.blocks || [];
      const localIds = new Set(blocks.map((b) => b.id));
      // delete blocks that no longer exist locally
      for (const cb of currentBlocks) {
        if (!localIds.has(cb.id)) {
          await api.delete(`/contract-templates/${id}/blocks/${cb.id}`);
        }
      }
      // add/update
      const idMap = {}; // tmp id -> real id (for state refresh)
      for (const b of blocks) {
        const payload = {
          title: b.title || "",
          body: b.body || "",
          category: b.category || null,
          optional: !!b.optional,
          enabled: b.enabled !== false,
          sort: b.sort,
          note: b.note || null,
        };
        if (b._isNew || !currentBlocks.find((cb) => cb.id === b.id)) {
          const { data: created } = await api.post(`/contract-templates/${id}/blocks`, payload);
          idMap[b.id] = created.id;
        } else {
          await api.patch(`/contract-templates/${id}/blocks/${b.id}`, payload);
        }
      }
      // 3. Reorder using final id list
      const finalOrder = blocks.map((b) => idMap[b.id] || b.id);
      if (finalOrder.length > 0) {
        await api.post(`/contract-templates/${id}/blocks/reorder`, { ids: finalOrder });
      }
      // Map the previously focused block id (possibly a temp id) to its
      // freshly-assigned server id so the user stays on the same block.
      const newActiveId = idMap[activeBlockId] || activeBlockId;
      toast.success("Gespeichert");
      await load(newActiveId);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportPdf = async () => {
    await exportTo("pdf");
  };
  const exportDocx = async () => {
    await exportTo("docx");
  };
  const exportTo = async (format) => {
    if (dirty) {
      const ok = await confirmDialog({
        title: "Ungespeicherte Änderungen?",
        description: "Vor dem Export sollte gespeichert werden. Trotzdem fortfahren?",
        confirmText: "Trotzdem exportieren", tone: "warn",
      });
      if (!ok) return;
    }
    try {
      const res = await fetch(`${API}/contract-templates/${id}/render?format=${format}`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${getToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const txt = await res.text();
        toast.error(`Export fehlgeschlagen: ${txt}`); return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ext = format === "pdf" ? "pdf" : "docx";
      a.href = url;
      a.download = `${tpl?.name || "Vertragsvorlage"}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!tpl) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center text-slate-500 text-sm">
        Lade Vertragsvorlage…
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-slate-50" data-testid="contract-editor">
      {/* Topbar */}
      <header className="px-3 lg:px-5 py-2 bg-white border-b border-slate-200 flex items-center gap-3 shrink-0">
        <button
          onClick={() => navigate("/vertragsvorlagen")}
          className="text-slate-500 hover:text-slate-900 p-1 -ml-1"
          data-testid="ce-back"
          title="Zurück"
        >
          <CaretLeft size={18} />
        </button>
        <Input
          value={tpl.name || ""}
          onChange={(e) => updateTplField("name", e.target.value)}
          className="h-8 text-sm font-semibold border-transparent hover:border-slate-200 focus:border-slate-300 max-w-md bg-transparent"
          data-testid="ce-name"
        />
        <select
          value={tpl.status || "draft"}
          onChange={(e) => updateTplField("status", e.target.value)}
          className="h-8 text-xs border border-slate-200 rounded-sm px-2 bg-white"
          data-testid="ce-status"
        >
          <option value="draft">Entwurf</option>
          <option value="active">Aktiv</option>
          <option value="archived">Archiviert</option>
        </select>
        <div className="ml-auto flex items-center gap-1.5">
          {dirty && <span className="text-[10px] text-amber-600">ungespeichert</span>}
          <Button
            onClick={save} disabled={saving || !dirty}
            size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1.5 h-8"
            data-testid="ce-save"
          >
            <FloppyDisk size={14} /> {saving ? "Speichere…" : "Speichern"}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={exportPdf} className="h-8 gap-1.5"
            data-testid="ce-export-pdf"
          >
            <FilePdf size={14} /> PDF
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={exportDocx} className="h-8 gap-1.5"
            data-testid="ce-export-docx"
          >
            <FileDoc size={14} /> DOCX
          </Button>
        </div>
      </header>

      {/* Settings bar */}
      <div className="px-3 lg:px-5 py-1.5 bg-white border-b border-slate-100 flex items-center gap-3 flex-wrap shrink-0 text-xs">
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 uppercase">Typ</span>
          <select
            value={tpl.contract_type || "wohnraum"}
            onChange={(e) => updateTplField("contract_type", e.target.value)}
            className="h-7 border border-slate-200 rounded-sm px-1.5 bg-white"
            data-testid="ce-type"
          >
            {CONTRACT_TYPES.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 uppercase">Schrift</span>
          <select
            value={tpl.font_family || "Helvetica"}
            onChange={(e) => updateTplField("font_family", e.target.value)}
            className="h-7 border border-slate-200 rounded-sm px-1.5 bg-white"
            data-testid="ce-font"
          >
            {["Helvetica", "Calibri", "Times", "Arial", "Georgia"].map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          <input
            type="number" min={8} max={14}
            value={tpl.font_size_pt || 11}
            onChange={(e) => updateTplField("font_size_pt", parseInt(e.target.value) || 11)}
            className="w-12 h-7 border border-slate-200 rounded-sm px-1.5 bg-white"
            data-testid="ce-fontsize"
          />
          <span className="text-[10px] text-slate-500">pt</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 uppercase">Beschreibung</span>
          <Input
            value={tpl.description || ""}
            onChange={(e) => updateTplField("description", e.target.value)}
            className="h-7 text-xs w-72"
            placeholder="z. B. Mietvertrag Wohnraum, Standardklauseln 2026"
            data-testid="ce-desc"
          />
        </div>
        <span className="ml-auto text-[10px] text-slate-500">
          {blocks.length} Baustein{blocks.length === 1 ? "" : "e"} ·
          {blocks.filter((b) => b.enabled !== false).length} aktiv
        </span>
      </div>

      {/* Main split */}
      <div className="flex-1 min-h-0 grid grid-cols-[320px_1fr]">
        {/* Block list */}
        <aside className="border-r border-slate-200 bg-white overflow-y-auto flex flex-col" data-testid="ce-block-list">
          <div className="sticky top-0 bg-white border-b border-slate-100 px-3 py-1.5 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Bausteine
            </span>
            <button
              onClick={addBlock}
              className="text-xs text-emerald-700 hover:bg-emerald-50 px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1"
              data-testid="ce-add-block"
            >
              <Plus size={12} /> Neu
            </button>
          </div>
          <ul className="flex-1 py-1">
            {blocks.length === 0 && (
              <li className="px-3 py-4 text-xs text-slate-400 italic text-center">
                Noch keine Bausteine.<br />
                <button
                  onClick={addBlock}
                  className="text-emerald-700 underline mt-1"
                  data-testid="ce-add-first"
                >
                  Ersten Baustein anlegen
                </button>
              </li>
            )}
            {blocks.map((b, idx) => {
              const isActive = b.id === activeBlockId;
              return (
                <li
                  key={b.id}
                  className={`group flex items-center gap-1 pl-2 pr-1 py-1 cursor-pointer text-xs border-l-2 ${
                    isActive
                      ? "border-emerald-500 bg-emerald-50/50"
                      : "border-transparent hover:bg-slate-50"
                  } ${b.enabled === false ? "opacity-50" : ""}`}
                  onClick={() => setActiveBlockId(b.id)}
                  data-testid={`ce-block-row-${b.id}`}
                >
                  <span className="w-5 text-[10px] text-slate-400 tabular-nums">{idx + 1}.</span>
                  <span className="flex-1 truncate font-medium text-slate-800">
                    {b.title || "Ohne Titel"}
                  </span>
                  {b.optional && (
                    <span className="text-[8px] uppercase text-amber-700 bg-amber-50 px-1 rounded-sm">
                      opt
                    </span>
                  )}
                  <div
                    className="opacity-0 group-hover:opacity-100 flex items-center gap-0.5 transition-opacity"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => moveBlock(idx, -1)}
                      disabled={idx === 0}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30 p-0.5"
                      data-testid={`ce-block-up-${b.id}`}
                      title="Hoch"
                    >
                      <ArrowUp size={11} />
                    </button>
                    <button
                      onClick={() => moveBlock(idx, +1)}
                      disabled={idx === blocks.length - 1}
                      className="text-slate-400 hover:text-slate-700 disabled:opacity-30 p-0.5"
                      data-testid={`ce-block-down-${b.id}`}
                      title="Runter"
                    >
                      <ArrowDown size={11} />
                    </button>
                    <button
                      onClick={() => deleteBlock(b)}
                      className="text-rose-400 hover:text-rose-700 p-0.5"
                      data-testid={`ce-block-delete-${b.id}`}
                      title="Löschen"
                    >
                      <Trash size={11} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Block editor */}
        <main className="flex flex-col min-h-0 overflow-hidden bg-white" data-testid="ce-block-editor">
          {activeBlock ? (
            <>
              {/* Block meta row */}
              <div className="px-4 py-2 border-b border-slate-100 flex items-center gap-2 flex-wrap shrink-0">
                <Input
                  value={activeBlock.title || ""}
                  onChange={(e) => updateBlockField("title", e.target.value)}
                  placeholder="Baustein-Titel (z. B. §3 Miete und Nebenkosten)"
                  className="h-8 text-sm font-semibold flex-1 min-w-[260px]"
                  data-testid="ce-block-title"
                />
                <div className="w-40">
                  <CategorySelect
                    value={activeBlock.category || ""}
                    onChange={(v) => updateBlockField("category", v)}
                    testid="ce-block-category"
                  />
                </div>
                <button
                  onClick={() => updateBlockField("optional", !activeBlock.optional)}
                  className={`text-xs px-2 py-1 rounded-sm inline-flex items-center gap-1 border ${
                    activeBlock.optional
                      ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-slate-50 text-slate-600 border-slate-200"
                  }`}
                  data-testid="ce-block-optional"
                >
                  {activeBlock.optional ? "Optional" : "Pflicht"}
                </button>
                <button
                  onClick={() => updateBlockField("enabled", activeBlock.enabled === false)}
                  className={`text-xs px-2 py-1 rounded-sm inline-flex items-center gap-1 border ${
                    activeBlock.enabled !== false
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : "bg-slate-100 text-slate-500 border-slate-200"
                  }`}
                  data-testid="ce-block-enabled"
                >
                  {activeBlock.enabled !== false ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  {activeBlock.enabled !== false ? "Aktiv" : "Inaktiv"}
                </button>
                <button
                  onClick={() => setSnippetPickerOpen(true)}
                  className="text-xs px-2 py-1 rounded-sm inline-flex items-center gap-1 border bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                  data-testid="ce-snippets"
                  title="Baustein aus Bibliothek einfügen"
                >
                  <Stack size={14} />
                  Baustein einfügen
                </button>
              </div>

              {/* Word-style ribbon — full Start / Einfügen / Layout */}
              <WordRibbon
                editor={editor}
                layout={{
                  marginTop: tpl.page_margin_top_mm,
                  marginBottom: tpl.page_margin_bottom_mm,
                  marginLeft: tpl.page_margin_left_mm,
                  marginRight: tpl.page_margin_right_mm,
                  orientation: tpl.page_orientation || "portrait",
                  pageFormat: tpl.page_format || "a4",
                  columns: tpl.page_columns || 1,
                  hyphenation: !!tpl.hyphenation,
                }}
                onLayoutChange={(patch) => {
                  const map = {
                    marginTop: "page_margin_top_mm",
                    marginBottom: "page_margin_bottom_mm",
                    marginLeft: "page_margin_left_mm",
                    marginRight: "page_margin_right_mm",
                    orientation: "page_orientation",
                    pageFormat: "page_format",
                    columns: "page_columns",
                    hyphenation: "hyphenation",
                  };
                  Object.entries(patch).forEach(([k, v]) => {
                    updateTplField(map[k] || k, v);
                  });
                }}
              />

              {/* Editor body */}
              <div className="flex-1 overflow-y-auto" data-testid="ce-block-body">
                <div className="max-w-[800px] mx-auto bg-white">
                  <EditorContent editor={editor} />
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
              <div className="text-center">
                <Eye size={24} className="mx-auto text-slate-300 mb-2" />
                Wähle links einen Baustein oder lege einen neuen an.
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Snippet picker dialog */}
      <SnippetPicker
        open={snippetPickerOpen}
        onOpenChange={setSnippetPickerOpen}
        currentTemplateId={id}
        onInsert={(html) => {
          if (editor) editor.chain().focus().insertContent(html).run();
        }}
      />
    </div>
  );
}
